import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Instagram, MapPin, Globe, Phone } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, SectionLabel, PrimaryCTA, ExpertCTA } from "@/components/ui-kit";
import { CONFIG, scrollToId, whatsappFor, NAV_LINKS } from "@/config";
import { SERVICES, AUDIENCE, STEPS, WHY, IG_POSTS, FAQS, FOUNDERS, MARQUEE_WORDS } from "@/data";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

export const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

/* ---------- TRUST STRIP (merged with "who it's for") ---------- */
export const TrustStrip = () => {
  const items = [
    { label: "Invest", icon: "TrendingUp" },
    { label: "Protect", icon: "ShieldCheck" },
    { label: "Borrow", icon: "HandCoins" },
    { label: "Plan", icon: "ClipboardList" },
    { label: "Grow", icon: "Sprout" },
  ];
  return (
    <section className="border-y border-navy/[0.07] bg-white py-14 sm:py-16">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-2xl font-bold text-navy sm:text-3xl">
              One place for your entire financial journey.
            </h2>
            <p className="mt-3 text-sm text-ink/60 sm:text-base">
              Financial decisions become easier when your investments, protection, borrowing and long-term goals work together.
            </p>
          </div>
        </Reveal>
        <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-4 md:grid-cols-5">
          {items.map((it, i) => (
            <Reveal key={it.label} delay={i * 0.06}>
              <div className="group flex flex-col items-center gap-2.5 rounded-2xl border border-navy/[0.07] bg-lightbg/60 px-3 py-5 text-center transition-transform duration-300 hover:-translate-y-1 sm:py-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-royal transition-colors group-hover:bg-royal group-hover:text-white">
                  <Icon name={it.icon} className="h-5 w-5" />
                </span>
                <span className="font-display text-base font-bold text-navy sm:text-lg">{it.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-col items-center gap-4 rounded-2xl border border-navy/[0.07] bg-lightbg/40 p-5 sm:flex-row sm:justify-between sm:p-6" data-testid="audience-strip">
            <p className="text-sm font-semibold text-navy">Who it's for:</p>
            <div className="flex flex-wrap justify-center gap-2 sm:justify-end">
              {AUDIENCE.map((a) => (
                <button
                  key={a.id}
                  data-testid={`audience-${a.id}`}
                  onClick={() => scrollToId("lead-form")}
                  title={a.line}
                  className="rounded-full border border-navy/10 bg-white px-4 py-2 text-sm font-medium text-navy/80 transition-colors hover:border-royal hover:text-royal"
                >
                  {a.title}
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------- SERVICE CARD (shared by home + services page) ---------- */
export const ServiceCard = ({ s, i }) => (
  <Reveal delay={(i % 4) * 0.08} className="h-full">
    <div
      data-testid={`service-${s.id}`}
      className="group flex h-full flex-col rounded-2xl border border-navy/[0.08] bg-white p-7 shadow-card transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-hover"
    >
      <div className="flex items-center justify-between">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/[0.05] text-royal transition-colors duration-300 group-hover:bg-royal group-hover:text-white">
          <Icon name={s.icon} className="h-6 w-6" />
        </span>
        <span className="font-display text-sm font-bold text-navy/20">{s.tag}</span>
      </div>
      <h3 className="mt-6 font-display text-xl font-bold text-navy">{s.title}</h3>
      {s.badge && (
        <span className="mt-1.5 inline-flex w-fit rounded-full bg-gold/15 px-2.5 py-0.5 text-[11px] font-semibold text-navy/80">
          {s.badge}
        </span>
      )}
      <p className="mt-2 text-[15px] leading-relaxed text-ink/65">{s.line}</p>
      {s.points.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2">
          {s.points.map((p) => (
            <span key={p} className="rounded-full bg-lightbg px-3 py-1 text-xs font-medium text-navy/70">
              {p}
            </span>
          ))}
        </div>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-x-5 gap-y-2 pt-6">
        <Link
          data-testid={`service-link-${s.id}`}
          to={`/services/${s.id}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy transition-colors hover:text-royal"
        >
          Learn more <ArrowRight className="h-4 w-4" />
        </Link>
        <a
          data-testid={`service-cta-${s.id}`}
          href={whatsappFor(s.title)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-semibold text-royal transition-colors hover:text-navy"
        >
          Enquire on WhatsApp
        </a>
      </div>
    </div>
  </Reveal>
);

/* ---------- SERVICES ---------- */
export const Services = () => (
  <section id="services" className="bg-lightbg py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <SectionLabel testId="services-label">Our Services</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
              Every financial solution, under one roof.
            </h2>
            <p className="mt-4 max-w-2xl text-base text-ink/65 sm:text-lg">
              From your first SIP to fixed income, protection, borrowing and advanced opportunities — Auxieron helps you make informed decisions at every stage.
            </p>
          </div>
          <Link
            to="/services"
            data-testid="services-view-all"
            className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-6 py-3 text-sm font-semibold text-navy transition-colors hover:border-navy/40"
          >
            View all services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>

      <div className="mt-10">
        <Carousel opts={{ align: "start", loop: false }} className="w-full" data-testid="services-carousel">
          <CarouselContent className="-ml-4">
            {SERVICES.map((s, i) => (
              <CarouselItem key={s.id} className="basis-[85%] pl-4 sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                <ServiceCard s={s} i={i} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="mt-6 flex items-center justify-between">
            <p className="text-xs text-ink/45">Swipe or use the arrows to explore all {SERVICES.length} services</p>
            <div className="flex gap-2">
              <CarouselPrevious data-testid="services-prev" className="static h-10 w-10 translate-y-0 rounded-full border-navy/15 bg-white text-navy hover:bg-navy hover:text-white" />
              <CarouselNext data-testid="services-next" className="static h-10 w-10 translate-y-0 rounded-full border-navy/15 bg-white text-navy hover:bg-navy hover:text-white" />
            </div>
          </div>
        </Carousel>
      </div>
    </div>
  </section>
);

/* ---------- WHO IS THIS FOR ---------- */
export const WhoIsFor = () => (
  <section id="audience" className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <SectionLabel testId="audience-label">Who is this for?</SectionLabel>
        <h2 className="mt-5 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
          Wherever you are in your financial journey.
        </h2>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {AUDIENCE.map((a, i) => (
          <Reveal key={a.id} delay={(i % 4) * 0.08}>
            <div
              data-testid={`audience-${a.id}`}
              className="group relative flex h-80 flex-col justify-end overflow-hidden rounded-2xl border border-navy/[0.08]"
            >
              <img
                src={a.img}
                alt={a.alt}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/10" />
              <div className="relative p-6">
                <h3 className="font-display text-xl font-bold text-white">{a.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/75">{a.line}</p>
                <button
                  data-testid={`audience-cta-${a.id}`}
                  onClick={() => scrollToId("lead-form")}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold"
                >
                  Find My Strategy <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- HOW IT WORKS ---------- */
export const HowItWorks = () => (
  <section id="how-it-works" className="bg-lightbg py-20 sm:py-28">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <Reveal>
        <SectionLabel testId="how-label">How it works</SectionLabel>
        <h2 className="mt-5 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
          Getting started is simple.
        </h2>
      </Reveal>

      <ol className="mt-14 flex snap-x snap-mandatory gap-0 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:overflow-visible" data-testid="how-roadmap">
        {STEPS.map((s, i) => (
          <li key={s.num} className="relative w-[78%] flex-none snap-start pr-5 sm:w-[52%] md:w-auto md:flex-1" data-testid={`how-step-${s.num}`}>
            <Reveal delay={i * 0.12}>
              <div className="flex items-center">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-navy font-display text-xl font-extrabold text-gold shadow-hover ring-4 ring-gold/25">
                  {s.num}
                </div>
                {i < STEPS.length - 1 && (
                  <div className="relative ml-3 h-0 flex-1 border-t-2 border-dashed border-navy/20">
                    <ArrowRight className="absolute -right-1 -top-[11px] h-5 w-5 text-gold" />
                  </div>
                )}
              </div>
              <h3 className="mt-6 font-display text-xl font-bold text-navy">{s.title}</h3>
              <p className="mt-2 max-w-xs text-[15px] leading-relaxed text-ink/65">{s.line}</p>
            </Reveal>
          </li>
        ))}
      </ol>

      <Reveal delay={0.1}>
        <div className="mt-12">
          <PrimaryCTA testId="how-cta">Start My Financial Journey</PrimaryCTA>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- MARQUEE ---------- */
export const Marquee = () => {
  const row = [...MARQUEE_WORDS, ...MARQUEE_WORDS];
  return (
    <section className="overflow-hidden bg-navy py-6">
      <div className="marquee-track">
        {row.map((w, i) => (
          <span key={`${w}-${i < MARQUEE_WORDS.length ? "a" : "b"}`} className="mx-8 inline-flex items-center gap-8 font-display text-xl font-bold tracking-tight text-white/85 sm:text-2xl">
            {w} <span className="text-gold">✦</span>
          </span>
        ))}
      </div>
    </section>
  );
};

/* ---------- WHY AUXIERON ---------- */
export const WhyAuxieron = () => (
  <section id="why" className="relative overflow-hidden bg-navy py-20 sm:py-28">
    <div className="grain absolute inset-0" />
    <div className="pointer-events-none absolute -right-20 top-10 h-72 w-72 rounded-full bg-royal/15 blur-3xl" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <SectionLabel dark testId="why-label">Why Auxieron</SectionLabel>
        <h2 className="mt-5 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          Financial decisions should feel simpler.
        </h2>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={(i % 4) * 0.08}>
            <div className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur-sm transition-colors duration-300 hover:border-gold/40">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/15 text-gold">
                <Icon name={w.icon} className="h-6 w-6" />
              </span>
              <h3 className="mt-6 font-display text-xl font-bold text-white">{w.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/70">{w.line}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- FOUNDERS ---------- */
export const Founders = () => (
  <section id="founders" className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-5xl px-5 sm:px-8">
      <Reveal>
        <SectionLabel testId="founders-label">Leadership</SectionLabel>
        <h2 className="mt-5 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
          The people behind Auxieron.
        </h2>
        <p className="mt-4 max-w-2xl text-base text-ink/65 sm:text-lg">
          Auxieron is led by AMFI-registered mutual fund distributors and BSE members, guiding you with clarity and accountability.
        </p>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2">
        {FOUNDERS.map((f, i) => (
          <Reveal key={f.name} delay={i * 0.1}>
            <div
              data-testid={`founder-${i}`}
              className="flex h-full flex-col rounded-2xl border border-navy/[0.08] bg-lightbg/60 p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-hover"
            >
              <div className="flex items-center gap-4">
                <img
                  src={f.photo}
                  alt={`${f.name}, ${f.role} of Auxieron`}
                  loading="lazy"
                  draggable="false"
                  className="pointer-events-none h-16 w-16 shrink-0 select-none rounded-2xl object-cover object-top ring-2 ring-navy/10"
                />
                <div>
                  <h3 className="font-display text-xl font-bold text-navy">{f.name}</h3>
                  <p className="text-sm font-medium text-royal">{f.role}</p>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="rounded-full border border-navy/10 bg-white px-3 py-1.5 text-xs font-semibold text-navy/75">
                  {f.arn}
                </span>
                <span className="rounded-full border border-navy/10 bg-white px-3 py-1.5 text-xs font-semibold text-navy/75">
                  {f.bse}
                </span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-xs text-ink/45">
        Mutual fund investments are subject to market risks. Distribution services provided under the ARNs listed above.
      </p>
    </div>
  </section>
);

/* ---------- INSTAGRAM ---------- */
export const InstagramSection = () => (
  <section id="instagram" className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <Reveal>
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <SectionLabel testId="ig-label">On Instagram</SectionLabel>
            <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
              Follow Auxieron on Instagram
            </h2>
            <p className="mt-4 max-w-lg text-base text-ink/65 sm:text-lg">
              Simple explanations. Smarter money decisions. Practical financial education.
            </p>
            <p className="mt-3 font-display text-lg font-bold text-royal">{CONFIG.instagramHandle}</p>
          </div>
          <a
            data-testid="ig-follow-cta"
            href={CONFIG.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#0d2c56]"
          >
            <Instagram className="h-4 w-4" /> Follow on Instagram
          </a>
        </div>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3">
        {IG_POSTS.map((p, i) => {
          const overlay = p.accent === "navy" ? "bg-navy/80" : p.accent === "gold" ? "bg-gold/80" : "bg-royal/80";
          const txt = p.accent === "gold" ? "text-navy" : "text-white";
          return (
            <Reveal key={p.topic} delay={i * 0.1}>
              <a
                href={CONFIG.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ig-post-${i}`}
                className="group block overflow-hidden rounded-2xl border border-navy/[0.08]"
              >
                <div className={`relative flex aspect-square flex-col justify-between overflow-hidden p-6 ${txt}`}>
                  <img
                    src={p.img}
                    alt={p.alt}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className={`absolute inset-0 ${overlay} mix-blend-multiply`} />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  <div className="relative flex items-center justify-between">
                    <Icon name={p.icon} className="h-8 w-8 opacity-95" />
                    <Instagram className="h-5 w-5 opacity-80" />
                  </div>
                  <div className="relative">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-80">Education</p>
                    <h3 className="mt-2 font-display text-2xl font-extrabold leading-tight">{p.topic}</h3>
                  </div>
                </div>
                <div className="bg-white p-5">
                  <p className="text-sm leading-relaxed text-ink/70">{p.caption}</p>
                </div>
              </a>
            </Reveal>
          );
        })}
      </div>
      <p className="mt-6 text-center text-xs text-ink/40">Educational previews. Visit our profile for the latest posts.</p>
    </div>
  </section>
);

/* ---------- FAQ ---------- */
export const FAQSection = ({ limit }) => (
  <section id="faq" className="bg-lightbg py-20 sm:py-28">
    <div className="mx-auto max-w-3xl px-5 sm:px-8">
      <Reveal>
        <SectionLabel testId="faq-label">FAQ</SectionLabel>
        <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
          Questions, answered.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="faq-accordion">
          {(limit ? FAQS.slice(0, limit) : FAQS).map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className="overflow-hidden rounded-2xl border border-navy/[0.08] bg-white px-5"
            >
              <AccordionTrigger className="py-5 text-left font-display text-base font-bold text-navy hover:no-underline sm:text-lg">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-[15px] leading-relaxed text-ink/65">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        {limit && limit < FAQS.length && (
          <Link to="/contact#faq" data-testid="faq-see-all" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-royal transition-colors hover:text-navy">
            See all {FAQS.length} questions <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </Reveal>
    </div>
  </section>
);

/* ---------- FINAL CTA ---------- */
export const FinalCTA = () => (
  <section className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-5xl px-5 sm:px-8">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] bg-navy px-6 py-16 text-center sm:px-16 sm:py-24">
          <div className="grain absolute inset-0" />
          <div className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 rounded-full bg-royal/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -right-16 h-64 w-64 rounded-full bg-gold/15 blur-3xl" />
          <div className="relative">
            <h2 className="mx-auto max-w-2xl font-display text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
              Your financial future deserves a plan.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base text-white/70 sm:text-lg">
              Start with your goals. We'll help you figure out the next step.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <PrimaryCTA testId="final-primary-cta" />
              <Link
                to="/book"
                data-testid="final-book-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-7 py-4 text-[15px] font-semibold text-white transition-colors hover:bg-white/10"
              >
                <Icons.CalendarDays className="h-4 w-4 text-gold" /> Book a free call
              </Link>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- FOOTER ---------- */
export const Footer = () => (
  <footer className="border-t border-navy/10 bg-lightbg">
    <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
      <div className="grid grid-cols-1 gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <img src={CONFIG.logo} alt="Auxieron Pvt. Ltd. logo" className="h-14 w-auto" />
          <p className="mt-5 max-w-sm font-display text-base font-bold text-navy">{CONFIG.tagline}</p>
          <p className="mt-2 max-w-sm text-sm leading-relaxed text-ink/60">{CONFIG.subline}</p>
          <div className="mt-5 space-y-2 text-sm text-ink/60">
            <p data-testid="footer-address" className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-royal" /> Office: {CONFIG.address}</p>
            <a href={CONFIG.phoneHref} data-testid="footer-phone" className="flex items-center gap-2 hover:text-navy">
              <Phone className="h-4 w-4 text-royal" /> {CONFIG.phone}
            </a>
            <a href={CONFIG.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-navy">
              <Globe className="h-4 w-4 text-royal" /> {CONFIG.websiteLabel}
            </a>
            <a href={CONFIG.instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-ig" className="flex items-center gap-2 hover:text-navy">
              <Instagram className="h-4 w-4 text-royal" /> Instagram — {CONFIG.instagramHandle}
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-display text-sm font-bold uppercase tracking-wide text-navy">Services</h4>
          <ul className="mt-4 space-y-2.5 text-sm text-ink/60">
            {SERVICES.map((s) => (
              <li key={s.id}>
                <Link to={`/services/${s.id}`} data-testid={`footer-service-${s.id}`} className="transition-colors hover:text-navy">{s.title}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm font-bold uppercase tracking-wide text-navy">Company</h4>
          <ul className="mt-4 space-y-2.5 text-sm text-ink/60">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={`footer-nav-${l.to.slice(1)}`} className="transition-colors hover:text-navy">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm font-bold uppercase tracking-wide text-navy">Legal</h4>
          <ul className="mt-4 space-y-2.5 text-sm text-ink/60">
            {[["Privacy Policy", "/privacy"], ["Terms & Conditions", "/terms"], ["Disclaimer", "/disclaimer"]].map(([l, to]) => (
              <li key={l}>
                <Link to={to} data-testid={`footer-link-${to.slice(1)}`} className="transition-colors hover:text-navy">{l}</Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-12 border-t border-navy/10 pt-8">
        <p className="text-xs leading-relaxed text-ink/45">
          <strong className="text-ink/60">Disclaimer:</strong> {CONFIG.legalName} provides financial services and guidance.
          Mutual fund and market-linked investments are subject to market risks; please read all scheme-related documents
          carefully before investing. Insurance is the subject matter of solicitation. Past performance is not indicative of
          future results, and no returns are guaranteed. Fixed-income instruments carry credit, interest-rate and liquidity
          risks. Loans are arranged through third-party lending partners — {CONFIG.legalName} is not a lender, and sanction,
          rates and terms are at the sole discretion of the lending partner. Nothing on this website constitutes an offer,
          recommendation or guarantee of any specific outcome.
        </p>
        <p className="mt-3 text-xs leading-relaxed text-ink/45">
          <strong className="text-ink/60">Regulatory details:</strong> AMFI-registered Mutual Fund Distributors —
          Rohan Kumar Sarbadhikari (ARN-354104, BSE Member Code 66455) and Madhurima Das (ARN-354103, BSE Member Code 66403).
        </p>
        <p className="mt-5 text-xs text-ink/45">© {new Date().getFullYear()} {CONFIG.legalName}. All rights reserved.</p>
      </div>
    </div>
  </footer>
);
